import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    CheckCircle,
    Building2,
    Flag,
    UserCog,
    BarChart3,
    Settings,
    Activity,
    Crown,
    Shield,
    ShieldAlert,
    MessageSquare,
    Ticket,
    Wrench,
    Trophy,
    Radio,
    UsersRound,
    User,
    ListOrdered,
    ChevronDown,
    ChevronRight,
    Bell,
} from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';

export default function Sidebar() {
    const { hasPrivilege, isSuperAdmin } = useAdmin();
    const location = useLocation();
    const [basketballExpanded, setBasketballExpanded] = useState(
        location.pathname.startsWith('/basketball')
    );

    // Check if admin has any social platform privileges (not just basketball)
    const hasSocialPrivilege =
        hasPrivilege('user_management') ||
        hasPrivilege('content_moderation') ||
        hasPrivilege('verification_approval') ||
        hasPrivilege('analytics_view') ||
        hasPrivilege('system_config');

    const navItems = [
        {
            name: 'Dashboard',
            path: '/',
            icon: LayoutDashboard,
            show: hasSocialPrivilege, // Only show if admin has social platform privileges
        },
        {
            name: 'Users',
            path: '/users',
            icon: Users,
            show: hasPrivilege('user_management'),
        },
        {
            name: 'Verifications',
            path: '/verifications',
            icon: CheckCircle,
            show: hasPrivilege('verification_approval'),
        },
        {
            name: 'Communities',
            path: '/communities',
            icon: Building2,
            show: hasPrivilege('verification_approval'),
        },
        {
            name: 'Reports',
            path: '/reports',
            icon: Flag,
            show: hasPrivilege('content_moderation'),
        },
        {
            name: 'Support Tickets',
            path: '/support',
            icon: Ticket,
            show: hasPrivilege('user_management'), // Support is user-related
        },
        {
            name: 'Reserved Usernames',
            path: '/reserved-usernames',
            icon: UserCog,
            show: hasPrivilege('user_management'),
        },
        {
            name: 'Analytics',
            path: '/analytics',
            icon: BarChart3,
            show: hasPrivilege('analytics_view'),
        },
        {
            name: 'Activity Logs',
            path: '/activity-logs',
            icon: Activity,
            show: isSuperAdmin(), // Only super admins can see audit trail
        },
        {
            name: 'System Config',
            path: '/system',
            icon: Settings,
            show: hasPrivilege('system_config'),
        },
        {
            name: 'Security',
            path: '/security',
            icon: ShieldAlert,
            show: hasPrivilege('system_config'),
        },
        {
            name: 'LBA Settings',
            path: '/lba-settings',
            icon: Crown,
            show: isSuperAdmin(),
        },
        {
            name: 'Popups',
            path: '/popups',
            icon: MessageSquare,
            show: hasPrivilege('system_config'),
        },
        {
            name: 'Notifications',
            path: '/notifications',
            icon: Bell,
            show: hasPrivilege('notification_management') || isSuperAdmin(),
        },
        {
            name: 'Admins',
            path: '/admins',
            icon: Shield,
            show: isSuperAdmin(),
        },
        {
            name: 'Maintenance',
            path: '/maintenance',
            icon: Wrench,
            show: isSuperAdmin(),
        },
    ];

    const basketballSubItems = [
        { name: 'Games', path: '/basketball/games', icon: Radio },
        { name: 'Teams', path: '/basketball/teams', icon: UsersRound },
        { name: 'People', path: '/basketball/people', icon: User },
        { name: 'Standings', path: '/basketball/standings', icon: ListOrdered },
        { name: 'Settings', path: '/basketball/settings', icon: Settings },
    ];

    const showBasketball = hasPrivilege('basketball_management');

    return (
        <aside className="w-64 bg-accent-light dark:bg-accent-dark border-r border-border-light dark:border-border-dark">
            <div className="p-6 border-b border-border-light dark:border-border-dark">
                <h1 className="text-xl font-bold">LBA Admin</h1>
            </div>

            <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-100px)]">
                {navItems
                    .filter((item) => item.show)
                    .map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === '/'}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive
                                    ? 'bg-foreground-light dark:bg-foreground-dark text-background-light dark:text-background-dark'
                                    : 'text-foreground-light dark:text-foreground-dark hover:bg-border-light dark:hover:bg-border-dark'
                                }`
                            }
                        >
                            <item.icon size={20} />
                            <span className="font-medium">{item.name}</span>
                        </NavLink>
                    ))}

                {/* Basketball Section */}
                {showBasketball && (
                    <div className="pt-2">
                        <button
                            onClick={() => setBasketballExpanded(!basketballExpanded)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${location.pathname.startsWith('/basketball')
                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                                : 'text-foreground-light dark:text-foreground-dark hover:bg-border-light dark:hover:bg-border-dark'
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <Trophy size={20} />
                                <span className="font-medium">Basketball</span>
                            </div>
                            {basketballExpanded ? (
                                <ChevronDown size={16} />
                            ) : (
                                <ChevronRight size={16} />
                            )}
                        </button>

                        {basketballExpanded && (
                            <div className="ml-4 mt-1 space-y-1 border-l-2 border-border-light dark:border-border-dark pl-3">
                                {basketballSubItems.map((item) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive
                                                ? 'bg-foreground-light dark:bg-foreground-dark text-background-light dark:text-background-dark'
                                                : 'text-foreground-light dark:text-foreground-dark hover:bg-border-light dark:hover:bg-border-dark'
                                            }`
                                        }
                                    >
                                        <item.icon size={16} />
                                        <span>{item.name}</span>
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </nav>
        </aside>
    );
}

